import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { List, ServerCog, Share2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useIntegrationsComputedAt, useProvidersStatus } from '@/lib/providers/use-providers';
import {
  readIntegrationsView,
  writeIntegrationsView,
  type IntegrationsView,
} from '@/lib/providers/integrations-view';
import { IntegrationsSection } from './integrations-section';
import { RecentTraffic } from './recent-traffic';
import { relativeTime } from './format';

/**
 * PROVEEDORES — what this environment uses for each service, in which mode, and
 * whether it answered.
 *
 * A fixed header (title, one line of purpose, when the answer was computed, the
 * List / Diagram choice) over ONE scroll region: the summary filters, the
 * attention line, one card per service stacked vertically, and — closed at the
 * bottom — the recent traffic per logical provider, pushed over the WS while
 * this page is open (the BFF runs its consolidating refresh only then).
 */
export function ProvidersRoute() {
  const { t } = useTranslation('network');
  // Joins the `providers` WS room while mounted; the table at the bottom reads it.
  const status = useProvidersStatus();
  // A passive reader of the section's inventory (no fetch of its own) — "updated X ago".
  const computedAt = useIntegrationsComputedAt();
  // The operator picks the shape; the list stays the default because it is the
  // denser and more actionable of the two. A stored choice that cannot be read
  // (locked-down storage) resolves to that same default rather than throwing.
  const [view, setView] = useState<IntegrationsView>(() => readIntegrationsView());

  const chooseView = (next: IntegrationsView): void => {
    setView(next);
    writeIntegrationsView(next);
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden p-4 sm:p-6">
      <header className="flex shrink-0 flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <ServerCog className="h-5 w-5 text-muted-foreground" aria-hidden />
            {t('providers.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('providers.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {computedAt ? (
            <span className="text-xs text-muted-foreground" data-testid="providers-updated">
              {t('providers.integrations.updated', { when: relativeTime(computedAt, t) })}
            </span>
          ) : null}
          <ViewToggle view={view} onChange={chooseView} />
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-2">
        <IntegrationsSection view={view} traffic={status.data?.providers} />
        <RecentTraffic query={status} />
      </div>
    </div>
  );
}

/**
 * List or diagram — the operator's call, not ours. The list answers "what is
 * wired, with what credentials, and does it respond?"; the diagram answers
 * "where does traffic actually go, and what never leaves this box?". The choice
 * is remembered (see `lib/providers/integrations-view.ts`).
 */
function ViewToggle({ view, onChange }: { view: IntegrationsView; onChange: (next: IntegrationsView) => void }) {
  const { t } = useTranslation('network');
  const options: { value: IntegrationsView; label: string; Icon: typeof List }[] = [
    { value: 'list', label: t('providers.diagram.viewList'), Icon: List },
    { value: 'diagram', label: t('providers.diagram.viewDiagram'), Icon: Share2 },
  ];

  return (
    <div
      role="group"
      aria-label={t('providers.diagram.viewLabel')}
      className="inline-flex items-center gap-0.5 rounded-md border border-border p-0.5"
      data-testid="integrations-view-toggle"
    >
      {options.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          aria-pressed={view === value}
          data-testid={`integrations-view-${value}`}
          className={cn(
            'inline-flex min-h-11 items-center gap-1.5 rounded px-3 text-xs font-medium transition-colors',
            view === value
              ? 'bg-secondary text-secondary-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          )}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden />
          {label}
        </button>
      ))}
    </div>
  );
}
